import React from "react";
import yup from "yup";
import { useObserver } from "mobx-react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid
} from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { Form, useFormStore } from "@modules/formx-react";
import { FormxDebugger } from "@common/formx";
import RegularButton from "@components/buttons/RegularButton";
import PrimaryButton from "@components/buttons/PrimaryButton";
import { Text } from "@components/formx";
import { DnDLocalContainer } from "@containers/DnDContainer";
import {
  AttachmentDto,
  TimereportExpenseDtoV2,
  timeReportServicePublic
} from "@api/timereports";
import axios from "axios";
import { store } from "@store";
import { ClearRounded } from "@material-ui/icons";

const schema = yup.object<TimereportExpenseDtoV2>({
  attachments: yup.array().of(yup.object<AttachmentDto>()),
  amount: yup.number().nullable(true).required(locale.fieldIsMandatory(locale.Sum)),
  comment: yup.mixed() // yup.string()
});

type Props = {
  open: boolean;
  initialValues: TimereportExpenseDtoV2 | null;
  onClose: () => void;
  onSubmit: (values: TimereportExpenseDtoV2) => void;
};

function getDecimalValue(value?: string | number): number {
  return parseFloat(parseFloat(String(value).replace(/,/g, ".")).toFixed(2));
}

export function DayExpenseForm(props: Props) {
  const form = useFormStore<TimereportExpenseDtoV2>({
    initialValues: props.initialValues || {
      attachments: [],
      comment: "",
      amount: null
    },
    schema,
    onSubmit: async () => {
      const data = {
        ...form.values,
        amount: getDecimalValue(form.values.amount)
      };
      props.onSubmit(data);
    }
  });

  const handleFiles = async (files: Blob[]) => {
    for (const file of files) {
      try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const arrayBuffer = await file.arrayBuffer();
        const result = await timeReportServicePublic.getSasUri({
          fileExt: file.type === "image/jpeg" ? "jpg" : "png"
        });
        const { uri, filePath } = result.data;

        await axios({
          baseURL: uri,
          method: "PUT",
          data: arrayBuffer,
          headers: {
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Methods": "PUT",
            "Access-Control-Allow-Headers": "*",
            "x-ms-blob-type": "BlockBlob"
          }
        });
        form.push("attachments", {
          path: filePath,
          isNew: true,
          previewUri: "",
          uri: URL.createObjectURL(file)
        });
      } catch (e) {
        console.log(e);
        store().snackbars.error(`Cannot upload files \n Reason:  ${e}`);
      }
    }
  };

  return useObserver(() => (
    <Dialog
      open={props.open}
      TransitionProps={{
        onExit: (node) => (node.style.overflow = "hidden"),
        onEntered: (node) => {
          node.style.overflow = "visible";
        }
      }}
      onClose={(_, reason) => {
        if (reason === "backdropClick") {
          return;
        }

        props.onClose();
      }}
      maxWidth="xs"
      fullWidth
    >
      <FormxDebugger store={form} />
      <DialogTitle>{locale.Edit}</DialogTitle>

      <DialogContent style={{ overflow: "auto" }}>
        <FormxDebugger store={form} />
        <Form store={form} allowUpdates>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Text label={locale.Total} name="amount" />
            </Grid>
            {form.values.attachments?.map((f, i) => (
              <Grid
                item
                xs={4}
                key={i}
                style={{
                  position: "relative",
                  background: `url(${f.uri})`,
                  borderRadius: "8px",
                  height: 115,
                  width: 115,
                  backgroundSize: "cover",
                  backgroundClip: "content-box",
                  overflow: "hidden"
                }}
              >
                <ClearRounded
                  color={"primary"}
                  style={{ position: "absolute", right: 5, top: 5 }}
                  onClick={() => {
                    form.removeByIndex("attachments", i);
                  }}
                />
              </Grid>
            ))}

            <Grid item xs={12}>
              <Text
                label={locale.Comment}
                name="comment"
                TextFieldProps={{
                  disabled: true
                }}
              />
            </Grid>

            <Grid
              item
              style={{
                border: "1px dotted",
                borderRadius: "30px",
                padding: 8,
                width: "100%",
                textAlign: "center"
              }}
            >
              <DnDLocalContainer
                multiple
                onDrop={(f) => handleFiles(f)}
                dropzoneOptions={{ accept: { "image/*": [".jpeg", ".png"] } }}
              >
                <div
                  style={{
                    height: 140,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  Drop file here
                </div>
              </DnDLocalContainer>
            </Grid>
          </Grid>
        </Form>
      </DialogContent>

      <DialogActions style={{ padding: "8px 24px" }}>
        <RegularButton onClick={props.onClose}>{locale.Cancel}</RegularButton>

        <PrimaryButton disabled={!form.canSubmit} onClick={() => form.submit()}>
          OK
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  ));
}
